import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@monksflow/monks-ui';
import { DownloadIcon, EyeIcon, MoreVerticalIcon, Trash2Icon } from 'lucide-react';
import type { FileRow } from '@/features/knowledge-bases/model';
import { useTranslation } from 'react-i18next';

interface KnowledgeBaseDocumentRowActionsProps {
  row: FileRow;
  onViewDetail: (row: FileRow) => void;
  onDownload: (row: FileRow) => void;
  onDelete: (row: FileRow) => void;
  canModify?: boolean;
}

/**
 * v4 row-actions dropdown for the KB documents table. Mirrors the actions of
 * the legacy MUI menu (View / Download / Delete) with the same disabled rules:
 * Download is unavailable when the file has no source asset (older files) or is
 * still uploading; Delete requires manage permission.
 */
export function KnowledgeBaseDocumentRowActions({
  row,
  onViewDetail,
  onDownload,
  onDelete,
  canModify = true,
}: KnowledgeBaseDocumentRowActionsProps) {
  const { t } = useTranslation('knowledge');

  const downloadDisabled = !row.assetId || !!row.isUploading;
  // Only the "source missing" case gets an explainer — an in-flight upload is
  // self-evidently not downloadable yet.
  const downloadTooltip =
    !row.assetId && !row.isUploading
      ? t('knowledgeBases.knowledgeBaseDocumentsTable.theSourceForThisFile')
      : '';

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="mini"
          aria-label={t('knowledgeBases.knowledgeBaseDocumentRowActions.documentActions')}
          className="size-9 p-0"
          onClick={(e) => e.stopPropagation()}
        >
          <MoreVerticalIcon className="size-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
        <DropdownMenuItem onSelect={() => onViewDetail(row)}>
          <EyeIcon className="mr-2 size-4 text-foreground" />
          {t('knowledgeBases.knowledgeBaseDocumentRowActions.viewDetail')}
        </DropdownMenuItem>
        {downloadTooltip ? (
          <TooltipProvider delayDuration={300}>
            <Tooltip>
              <TooltipTrigger asChild>
                {/* Wrapper span keeps the tooltip reachable while the item is disabled. */}
                <span>
                  <DropdownMenuItem disabled onSelect={() => onDownload(row)}>
                    <DownloadIcon className="mr-2 size-4 text-foreground" />
                    {t('knowledgeBases.knowledgeBaseDocumentRowActions.download')}
                  </DropdownMenuItem>
                </span>
              </TooltipTrigger>
              <TooltipContent>{downloadTooltip}</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        ) : (
          <DropdownMenuItem disabled={downloadDisabled} onSelect={() => onDownload(row)}>
            <DownloadIcon className="mr-2 size-4 text-foreground" />
            {t('knowledgeBases.knowledgeBaseDocumentRowActions.download')}
          </DropdownMenuItem>
        )}
        <DropdownMenuItem
          variant="destructive"
          disabled={!canModify}
          onSelect={() => onDelete(row)}
        >
          <Trash2Icon className="mr-2 size-4" />
          {t('knowledgeBases.knowledgeBaseDocumentRowActions.delete')}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

KnowledgeBaseDocumentRowActions.displayName = 'KnowledgeBaseDocumentRowActions';
